import { Button, Skeleton, Typography } from '@mui/material'
import moment from 'moment'
import React from 'react'
import CheckIcon from '@mui/icons-material/Check'
import InfoIcon from '@mui/icons-material/Info'
import AnnouncementIcon from '@mui/icons-material/Announcement'
import { displaySnackbar } from '../../../../actions/main'
import { useAppDispatch } from '../../../../store'
import { searchCities } from '../../../../utils/birthCities'
import insi from '../../../../utils/insi'
import TTDError from '../../../../utils/ttdError'
import DoubleButton from '../../../Common/DoubleButton/DoubleButton'
import ButtonConfigModal from '../../../INSi/ButtonConfigModal'
import INSiConfigModal from '../../../INSi/INSiConfigModal'
import { usePatientFormContext } from '../../context/PatientFormContext'
import PatientFormField from '../PatientFormField'
import { SetPatientInfoInsiResult } from '../_props'
import { InsOidLabelValueRecord, useFormContextPatient } from '../_utils'
import { PatientIdentityVerificatorProps, HandleMissingInsiData } from './_props'
import useStyles from './_styles/PatientIdentityVerificatorStyles'
import useCheckPatientStatus from './_utils/useCheckPatientStatus'
import { getIdentityStatusSlug, handleDate, shouldDirty } from './_utils/_utils'
import useTraceInsiCall from '../../../INSi/useTraceInsiCall'
import { useRegradeIdentity } from '../services/usePatientUtils'
import FullScreenActionDialog from '../../../Common/fullscreenActionDialog/FullScreenActionDialog'
import { ComparisonTableRowProps } from '../../../INSi/comparisonTable/_props'
import { FullScreenActionDialogButton } from '../../../Common/fullscreenActionDialog/_props'
import PatientInsiComparisonError from '../patientInsiComparisonDialog/PatientInsiComparisonError'
import ComparisonTable from '../../../INSi/comparisonTable/ComparisonTable'
import { createComparisonTable, fullscreenActionDialogValues } from '../patientInsiComparisonDialog/_utils'
import { ComparisonDialogError, PatientInsiComparisonTableValues } from '../patientInsiComparisonDialog/_props'
import { CalloutProps } from '../../../Common/callout/_props'
import { defaultPalette } from '../../../../theme/idsTheme'
import useCurrentUser from '../../../../hooks/useCurrentUser'

const PatientIdentityVerificator = ({ isLocked, setIdentity, onIdentityCheck, onInsiSuccess }: PatientIdentityVerificatorProps) => {
    const classes = useStyles()
    const dispatch = useAppDispatch()
    const user = useCurrentUser()
    const { state, update } = usePatientFormContext()
    const { regradeIdentity } = useRegradeIdentity()

    const { traceInsiCall } = useTraceInsiCall({
        patientId: state.patientId,
    })

    const [isINSiConfigModalOpen, setIsINSiConfigModalOpen] = React.useState(false)
    const [isINSiConfigModalWithPatients, setIsINSiConfigModalWithPatients] = React.useState(false)
    const [insiResult, setInsiResult] = React.useState<any>(undefined)
    const [comparisonDialogOpen, setComparisonDialogOpen] = React.useState(false)
    const [comparisonDialogMissingField, setComparisonDialogMissingField] = React.useState(false)
    const [comparisonDialogError, setComparisonDialogError] = React.useState<ComparisonDialogError | undefined>(
        undefined,
    )

    const [comparisonDialogValues, setComparisonDialogValues] = React.useState<
        PatientInsiComparisonTableValues | undefined
    >(undefined)

    const cancelButton: FullScreenActionDialogButton = {
        label: comparisonDialogMissingField ? 'Fermer' : 'Annuler',
        colorVariant: 'light',
        action: () => handleCancel(),
    }
    const saveButton: FullScreenActionDialogButton = {
        label: 'Mettre à jour',
        icon: <CheckIcon />,
        colorVariant: 'dark',
        action: () => fullScreenSaveButtonAction(),
    }

    const callout: CalloutProps = {
        icon: comparisonDialogMissingField ? <AnnouncementIcon /> : <InfoIcon />,
        color: comparisonDialogMissingField ? defaultPalette.error.main : defaultPalette.primary.main,
        backgroundColor: comparisonDialogMissingField
            ? defaultPalette.error.ultraLight
            : defaultPalette.primary.ultraFreackingLighter,
        children: (
            <Typography>
                {comparisonDialogMissingField
                    ? fullscreenActionDialogValues.calloutErrorTitle
                    : fullscreenActionDialogValues.calloutTitle}
            </Typography>
        ),
    }

    const slug = getIdentityStatusSlug(state.ins.identityStatus?.code ?? undefined)

    const { checkStatus } = useCheckPatientStatus(slug)

    const { mode } = state
    const { storedData, isDmpConnectorAlive } = state.ins

    insi.setMonitoringCpxCallback(() => {
        dispatch(displaySnackbar('error', 'La carte CPS a été retirée'))
    })

    const { watch, setValue, trigger } = useFormContextPatient()

    const setStoredData = (data: any) => {
        update({
            ins: {
                ...state.ins,
                storedData: data,
            },
        })
    }

    const formValues = watch()
    const watched = {
        insNum: formValues.insNum,
        insOid: formValues.insOid,
        birthName: formValues.birthName,
        firstNameList: formValues.firstNameList,
        sex: formValues.sex,
        birthDate: formValues.birthDate,
        birthCityCom: formValues.birthCity?.com,
        identityProof: formValues.identityProof,
    }

    /**
     * @async
     * @description Set patient with insi informations
     * @param {Object} patientInfoInsi patient information from inis web service
     * @returns {void}
     */
    const setPatientInfoInsi = async (patientInfoInsi: any): Promise<SetPatientInfoInsiResult | undefined> => {
        let birthPlaces = await searchCities(patientInfoInsi.birthPlace, patientInfoInsi.birthday, true)

        let birthCityCode = ''

        if (birthPlaces?.length === 0 && patientInfoInsi.birthPlace.startsWith('20')) {
            let postalCode = patientInfoInsi.birthPlace
            postalCode = `2A${patientInfoInsi.birthPlace.substr(2, 3)}`
            birthPlaces = await searchCities(postalCode, patientInfoInsi.birthday, true)
            birthCityCode = patientInfoInsi.birthPlace
        }

        if (birthPlaces?.length === 0 && patientInfoInsi.birthPlace.startsWith('20')) {
            let postalCode = patientInfoInsi.birthPlace
            postalCode = `2B${patientInfoInsi.birthPlace.substr(2, 3)}`
            birthPlaces = await searchCities(postalCode, patientInfoInsi.birthday, true)
            birthCityCode = patientInfoInsi.birthPlace
        }

        const birthPlace = birthPlaces[0] || undefined

        if (!birthCityCode && birthPlaces.length > 0) {
            birthCityCode = birthPlace?.com ?? ''
        }

        const lastName =
            patientInfoInsi.birthName &&
            patientInfoInsi.lastName &&
            patientInfoInsi.birthName !== patientInfoInsi.lastName
                ? patientInfoInsi.lastName
                : ''
        const birthName = patientInfoInsi.birthName ? patientInfoInsi.birthName : undefined

        const firstNameList = patientInfoInsi.firstNames?.split(' ')

        setValue('insOid', patientInfoInsi.ins_oid, shouldDirty)
        setValue('insHistoryNumRaw', patientInfoInsi.history || null, shouldDirty)
        setValue('insNum', `${patientInfoInsi.ins}${patientInfoInsi.ins_key}`, shouldDirty)
        setValue('socialSecurityNumber', `${patientInfoInsi.ins}${patientInfoInsi.ins_key}`, shouldDirty)
        setValue('birthCityLabel', birthPlaces && birthPlaces.length ? birthPlace?.libelle : '', shouldDirty)
        setValue('birthCityCode', birthCityCode, shouldDirty)
        setValue(
            'birthCity',
            {
                com: birthPlace?.com,
                libelle: birthPlace?.libelle,
            },
            shouldDirty,
        )
        setValue('birthDate', patientInfoInsi.birthday, shouldDirty)

        if (patientInfoInsi.sex) setValue('sex', patientInfoInsi.ins.charAt(0) === '1' ? 'm' : 'f', shouldDirty)

        setValue('lastName', lastName, shouldDirty)
        setValue('birthName', birthName, shouldDirty)

        if (firstNameList?.length > 0) {
            setValue('firstNameList', firstNameList, shouldDirty)
            setValue('firstFirstName', firstNameList[0], shouldDirty)
            setValue('firstName', firstNameList[0], shouldDirty)
        }

        const oidType = InsOidLabelValueRecord[patientInfoInsi.ins_oid]

        const identitySlug = regradeIdentity('set', watched.identityProof?.level ?? 1, ['QUALIFIED', 'RECOVERED'])

        await trigger()
        return {
            oidType,
            identitySlug,
        }
    }

    const traceInsiCallFromIdentityInformation = async () => {
        await traceInsiCall.getPatientInsIdentityFromIdentityInformation({
            data: insiResult.raw,
            delayed: state.mode === 'CREATION',
        })
    }

    const fullScreenSaveButtonAction = async () => {
        if (!insiResult) return

        if (insiResult.identityResult === 2 || insiResult.raw.i_insIdentityResult === 2) {
            setPatientInfoInsi(insiResult).then(async (res) => {
                if (res) {
                    const { oidType, identitySlug } = res
                    await setIdentity(identitySlug, undefined, {
                        oidType,
                    })
                }
            })
        }
        if (state.callType === 'IDENTITY_TRAITS') {
            await traceInsiCallFromIdentityInformation()
        }

        setComparisonDialogOpen(false)
        dispatch(
            displaySnackbar(
                'success',
                'L’identité et le statut INS du patient ont bien été mis à jour,  veuillez enregistrer la fiche patient pour les sauvegarder',
            ),
        )
    }

    const handleConfiguration = () => {
        setIsINSiConfigModalOpen(true)
        setIsINSiConfigModalWithPatients(false)
    }

    const getInsWithVitaleCard = () => {
        setIsINSiConfigModalOpen(true)
        setIsINSiConfigModalWithPatients(true)
    }

    /**
     * @description display snackbar when we get ins patient data
     * @param {Number} value
     * @returns {void}
     */
    const displayInsSnackBar = (type: number) => {
        if (type === 2 && !comparisonDialogMissingField)
            dispatch(displaySnackbar('success', "L'identité de votre patient a été récupérée avec succès."))
    }

    const handleFullScreenDialog = (result: any) => {
        let sex
        if (result.sex === 2) {
            sex = 'M'
        } else if (result.sex === 3) {
            sex = 'F'
        }
        const firstNameList = watched.firstNameList?.map((el) => (el?.__typename === 'Element' ? el?.value : el))

        setComparisonDialogValues({
            current: {
                insNum: watched.insNum || new Error('Inconnu'),
                insOid: watched.insOid || new Error('Inconnu'),
                birthName: watched.birthName || new Error('Inconnu'),
                firstNameList: firstNameList?.join(' ') || new Error('Inconnu'),
                sex: watched.sex?.toUpperCase() || new Error('Inconnu'),
                birthDate: handleDate(watched.birthDate ?? ''),
                birthCityCode: watched.birthCityCom || new Error('Inconnu'),
            },
            new: {
                insNum: result.ins || new Error('Donnée manquante'),
                insOid: result.ins_oid || new Error('Donnée manquante'),
                birthName: result.birthName || new Error('Donnée manquante'),
                firstNameList: result.firstNames || new Error('Donnée manquante'),
                sex: sex || new Error('Donnée manquante'),
                birthDate: handleDate(result.birthday),
                birthCityCode: result.birthPlace || new Error('Donnée manquante'),
            },
        })

        setComparisonDialogOpen(!comparisonDialogOpen)
    }

    const insiComparisonTable: ComparisonTableRowProps[] = React.useMemo(() => {
        if (comparisonDialogValues?.current || comparisonDialogValues?.new) {
            return createComparisonTable(comparisonDialogValues?.current, comparisonDialogValues?.new)
        }
        return []
    }, [comparisonDialogValues])

    const getInsWithoutVitaleCard = async () => {
        const success = await trigger()

        if (!success) {
            dispatch(displaySnackbar('error', 'Veuillez corriger les champs manquants'))
            return
        }

        update({
            loadingMessage: "Récupération de l'identité",
            callType: 'IDENTITY_TRAITS',
        })
        try {
            await insi.openSession()
            await insi.readCPxCard(storedData.cpsCard.index, storedData.cpsCard.name, storedData.pin)
            const result = await insi.searchPatientWithoutVitaleCard(
                watched.birthName,
                watched.firstNameList,
                watched.sex,
                moment(watched.birthDate).format('YYYY-MM-DD'),
                watched.birthCityCom,
                true,
            )
            const isFieldIsMissing = await handleMissingInsiData(result)

            setInsiResult(result)

            if (result.error) {
                setComparisonDialogError({
                    message:
                        'Désolé, une erreur provenant du téléservice INSi empêche de récupérer les informations du patient.',
                    helperText: `Code: ${result.error}`,
                })
            }

            if (result.identityResult === 1) {
                setComparisonDialogError({
                    message:
                        'Aucune identité n’a été trouvée pour ce patient : veuillez vérifier les informations patient renseignées et réessayer.',
                })
            } else if (result.identityResult !== 1 && result.identityResult !== 2) {
                setComparisonDialogError({
                    message:
                        'Plusieurs identités ont été trouvées pour ce patient : veuillez affiner votre recherche (par exemple, en ajoutant des prénoms de naissance) et réessayer.',
                })
            }

            handleFullScreenDialog(result)

            if (!isFieldIsMissing) {
                displayInsSnackBar(result.identityResult)
            }
        } catch (error) {
            if (error && error instanceof TTDError && error.message === 'Aucune carte') {
                const data = {
                    cpsCard: '',
                    vitaleCard: storedData.vitaleCard,
                    pin: '',
                    expire: storedData.expire,
                }
                insi.setStoredData(user, data)
                setStoredData(data)
                dispatch(
                    displaySnackbar(
                        'error',
                        'Carte CPS absente, veuillez réintroduire votre carte CPS et saisir le code porteur !',
                    ),
                )
            } else {
                dispatch(displaySnackbar('error', (error as Error).message))
            }
        } finally {
            update({
                loadingMessage: undefined,
            })
        }
    }

    const readCPxCardAndCheckPatientIdentity = async (ins: string, key: string, rawResponse = false) => {
        await insi.openSession()
        await insi.readCPxCard(storedData.cpsCard.index, storedData.cpsCard.name, storedData.pin)

        const birthdate = (watched.birthDate ? new Date(watched.birthDate) : new Date()).toISOString().slice(0, 10)

        const result = await insi.checkPatientIdentity(
            ins,
            key,
            watched.insOid,
            watched.birthName?.toUpperCase(),
            watched.firstNameList,
            watched.sex,
            birthdate,
            watched.birthCityCom,
            rawResponse,
        )
        return result
    }

    const checkPatientIdentity = async (ins: string | undefined, key: string | undefined) => {
        if (ins && key) {
            const res = await readCPxCardAndCheckPatientIdentity(ins, key, true)
            return res
        }
        throw new TTDError(`Impossible de vérifier l'identité patient. Numéro INS et clé manquants`)
    }

    const handleVerifyIns = async () => {
        const ins = watched.insNum ? watched.insNum.substring(0, watched.insNum.length - 2) : undefined
        const key = watched.insNum ? watched.insNum.substring(watched.insNum.length - 2) : undefined
        update({
            loadingMessage: "Vérification de l'identité INS",
        })

        try {
            const result = await checkPatientIdentity(ins, key)

            if (result?.s_status === 'OK') {
                await onIdentityCheck(false)
                update({
                    loadingMessage: undefined,
                })
                dispatch(displaySnackbar('success', "L'identité de votre patient a été vérifié avec succès."))
            } else {
                if (slug === 'RECOVERED' || slug === 'QUALIFIED') {
                    await onIdentityCheck(true)
                    await setIdentity('PROVISIONAL', {
                        loadingMessage: undefined,
                    })
                } else {
                    update({
                        loadingMessage: undefined,
                    })
                }
                dispatch(displaySnackbar('error', "L'identité de votre patient n'a pas pu être vérifié."))
            }
            await traceInsiCall.verifyPatientInsIdentity({ data: result })
        } catch (error) {
            if (error && error instanceof TTDError && error.message === 'Aucune carte') {
                const data = {
                    cpsCard: '',
                    vitaleCard: storedData.vitaleCard,
                    pin: '',
                    expire: storedData.expire,
                }
                insi.setStoredData(user, data)
                setStoredData(data)
                dispatch(
                    displaySnackbar(
                        'error',
                        'Carte CPS absente, veuillez réintroduire votre carte CPS et saisir le code porteur !',
                    ),
                )
            } else {
                dispatch(displaySnackbar('error', (error as Error).message))
            }
            update({
                loadingMessage: undefined,
            })
        }
    }

    const handleMissingInsiData = async (patientRes: HandleMissingInsiData) => {
        const unknownSex = patientRes.sex === 1
        const date = handleDate(patientRes.birthday)
        const invalidDate = date instanceof Error
        const missingInsiData =
            !patientRes.birthName || !patientRes.firstNames || !patientRes.birthday || unknownSex || invalidDate

        setComparisonDialogMissingField(missingInsiData)
        return missingInsiData
    }

    const handleCancel = async () => {
        const isCreation = state.mode === 'CREATION'
        const isIdentityTraitsVerification = state.callType === 'IDENTITY_TRAITS'

        try {
            if (!isCreation && isIdentityTraitsVerification) {
                await traceInsiCallFromIdentityInformation()
            }
        } catch (error) {
            console.error('Error while tracing INSi call on cancel', error)
        }
        setComparisonDialogOpen(false)
    }

    const withVitaleCardDisabled =
        !storedData ||
        !storedData.cpsCard ||
        !storedData.pin ||
        !storedData.vitaleCard ||
        !isDmpConnectorAlive ||
        isLocked

    const withoutVitaleCardDisabled =
        !storedData || !storedData.cpsCard || !storedData.pin || !isDmpConnectorAlive || isLocked

    const renderLoader = () => {
        const baseWidth = 10
        return (
            <>
                <Skeleton variant="rounded" width={`${baseWidth}rem`} height="3.5rem" />
                <Skeleton variant="rounded" width={`${baseWidth * 2}rem`} height="3.5rem" />
                <Skeleton variant="rounded" width={`${baseWidth / 3}rem`} height="3.5rem" />
            </>
        )
    }

    const renderValidatorLabelSideComponent = (type: 'large' | 'default') => (
        <>
            <ButtonConfigModal
                onClick={handleConfiguration}
                storedData={storedData}
                disabled={!isDmpConnectorAlive}
                withLabel={type === 'large'}
                largeLabel
                type={type}
            />
            {isINSiConfigModalOpen && (
                <INSiConfigModal
                    withPatients={isINSiConfigModalWithPatients}
                    setPatientInfoInsi={(res) => {
                        setInsiResult(res)
                        handleFullScreenDialog(res)
                    }}
                    onStoredDataChange={setStoredData}
                    onClose={() => {
                        setIsINSiConfigModalOpen(false)
                    }}
                    onSuccess={onInsiSuccess}
                />
            )}
        </>
    )

    return (
        <>
            <PatientFormField
                variant="custom"
                name="insNum"
                label={checkStatus(['PROVISIONAL', 'VALIDATED']) ? 'Récupérer INS' : 'INS'}
                tooltipTitle={
                    <p>
                        Pour pouvoir bénificier de la fonctionnalité permettant de récupérer l'identité INS du patient à
                        partir de la carte vitale et du téléservice INSi, vous devez préalablement installer le
                        composant téléchargeable à cette adresse :
                        <br />
                        <br />
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://www.icanopee.fr/telechargement-du-connecteur-dmpconnect-js2/"
                        >
                            https://www.icanopee.fr/telechargement-du-connecteur-dmpconnect-js2/
                        </a>
                    </p>
                }
                size="1"
                status={{
                    error: undefined,
                    valid: false,
                }}
                render={() => (
                    <div className={classes.PatientIdentityVerificator}>
                        {state.ins.isGettingConnector ? (
                            renderLoader()
                        ) : (
                            <>
                                {mode === 'EDITION' && checkStatus(['RECOVERED', 'QUALIFIED']) && (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleVerifyIns}
                                        disabled={!isDmpConnectorAlive}
                                    >
                                        VÉRIFIER IDENTITÉ INS
                                    </Button>
                                )}
                                {!checkStatus(['QUALIFIED', 'RECOVERED']) ? (
                                    <>
                                        <DoubleButton
                                            leftButton={{
                                                label: 'Avec carte vitale',
                                                disabled: withVitaleCardDisabled,
                                                onClick: getInsWithVitaleCard,
                                            }}
                                            rightButton={{
                                                label: 'Sans carte vitale',
                                                disabled: withoutVitaleCardDisabled,
                                                onClick: getInsWithoutVitaleCard,
                                            }}
                                        />
                                        {renderValidatorLabelSideComponent('default')}
                                    </>
                                ) : (
                                    renderValidatorLabelSideComponent('large')
                                )}
                            </>
                        )}
                    </div>
                )}
            />

            <FullScreenActionDialog
                open={comparisonDialogOpen}
                title={fullscreenActionDialogValues.title}
                cancelProps={cancelButton}
                saveProps={!comparisonDialogMissingField ? saveButton : undefined}
                isError={Boolean(comparisonDialogError?.message) || comparisonDialogMissingField}
                callout={!comparisonDialogError ? callout : undefined}
            >
                {/* if error display error instead of table, error state not defined for the fullscreen dialog for the moment  */}
                {comparisonDialogError && (
                    <PatientInsiComparisonError label={comparisonDialogError.message} helperText="" />
                )}
                {!comparisonDialogError?.message && insiComparisonTable && (
                    <ComparisonTable data={insiComparisonTable} />
                )}
            </FullScreenActionDialog>
        </>
    )
}

export default PatientIdentityVerificator
